import React from 'react';
import { motion } from 'framer-motion';
import { useScrollReveal } from '../../hooks/useScrollAnimations';
import PantryMap from './PantryMap';

const FindingsSection: React.FC = () => {
  const { ref: titleRef, isInView: titleInView } = useScrollReveal(0.3);
  const { ref: captionRef, isInView: captionInView } = useScrollReveal(0.3);

  return (
    <section className="tmf-findings">
      <div className="tmf-container">
        {/* Section Title */}
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 50 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="tmf-section-header"
        >
          <h2 className="tmf-section-title">What We Saw</h2>
        </motion.div>

        {/* Pantry Map */}
        <div className="tmf-findings-map">
          <PantryMap />
        </div>

        {/* Map Caption */}
        <motion.div
          ref={captionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={captionInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="tmf-map-caption"
        >
          <p>
            We mapped out the entire pantry layout during our site visit. The map shows how the space is organized: the main user access area where clients pick their groceries, the volunteer-only packing zone in the back, refrigerated sections for fresh produce and proteins, regular racks for shelf-stable items, and the flow from the foot path to the car driveway. The stars mark key interaction points where we observed volunteers helping clients, moments of connection, and where people made decisions about what to take home.
          </p>
        </motion.div>

        {/* Observations Content */}
        <div className="tmf-findings-content">
          {/* Card 1: Journey-focused design with stats */}
          <motion.div
            className="tmf-observation-card-journey"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="tmf-journey-card-header">
              <h3>They Come for Food. They Leave Without Knowing.</h3>
            </div>

            <div className="tmf-journey-grid">
              <div className="tmf-journey-stages-row">
                <div className="tmf-journey-stage">
                  <span className="tmf-stage-label">Outside</span>
                  <p>
                    First-time visitors appeared hesitant, standing outside unsure of the process or whether they were allowed in. One woman approached us asking which door to use. A couple came looking for clothes for their daughter's birthday after their wallet was stolen. It was their first time, and they weren't sure if they could just walk in. Even before entering, people were already uncertain about what this place was and what it offered.
                  </p>
                </div>

                <div className="tmf-journey-stage">
                  <span className="tmf-stage-label">Inside</span>
                  <p>
                    Inside, people were given bags and went straight to the racks. Eyes on the produce, the protein, the shelf-stable items. There's a books desk near the entrance. Free children's books, cookbooks. Out of 30 people we watched, only two stopped to look. Posters on the walls advertised benefit enrollment, case management, financial counseling. Multilingual signs explaining services. People didn't glance at them. <strong>They were here for food, and food only.</strong>
                  </p>
                </div>
              </div>

              <div className="tmf-journey-bottom-row">
                <div className="tmf-journey-stats">
                  <div className="tmf-stat-callout">
                    <div className="tmf-stat-number">2/30</div>
                    <div className="tmf-stat-label">looked at ad boards and information posters</div>
                  </div>
                </div>

                <div className="tmf-journey-quote">
                  <div className="tmf-quote-mark">"</div>
                  <p>Other than the casual greetings and introductions of what to take and not, not a lot of conversations happened between volunteers and people.</p>
                  <span className="tmf-quote-attribution">— Observation Notes</span>
                </div>
              </div>
            </div>

            <div className="tmf-journey-insight">
              <span className="tmf-insight-label">The core problem:</span> People come in with a singular focus. Get food, get out. They're not browsing. They're not reading posters. Volunteers try to inform people, but they get busy during pantry open hours. There's no consistent emphasis on sharing service information. <strong>Services are there, but awareness isn't.</strong> The gap between what's available and what people know about is massive.
            </div>
          </motion.div>

          {/* Card 2: Relationship-focused design with quotes */}
          <motion.div
            className="tmf-observation-card-relationships"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h3>The Warmth That Doesn't Translate</h3>

            <div className="tmf-relationships-content">
              <div className="tmf-warmth-quotes">
                <div className="tmf-floating-quote">"See you next week"</div>
                <div className="tmf-floating-quote">"How are you doing today?"</div>
                <div className="tmf-floating-quote">"How's life been?"</div>
              </div>

              <p>
                Southeast Community Services has built something rare: genuine community. Volunteers recognize regulars. They greet people warmly. These aren't scripted greetings. They're real relationships, built over months and years of weekly visits. People trust these volunteers. When a young man looked anxious, a volunteer approached: "How's life been?" Not "How can I help you?" <em>Life.</em> They care.
              </p>

              <div className="tmf-relationship-split">
                <div className="tmf-relationship-col tmf-col-positive">
                  <svg className="tmf-col-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="#20c997" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="tmf-col-label">What exists</div>
                  <ul className="tmf-relationship-list">
                    <li>Volunteers recognize regulars</li>
                    <li>Years of weekly visits</li>
                    <li>Genuine care and warmth</li>
                    <li>Familiarity and community</li>
                    <li>People helping each other</li>
                  </ul>
                </div>

                <div className="tmf-relationship-col tmf-col-gap tmf-col-negative">
                  <svg className="tmf-col-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="9" x2="12" y2="13" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="17" r="0.5" fill="#ef4444" stroke="#ef4444"/>
                  </svg>
                  <div className="tmf-col-label">What's missing</div>
                  <ul className="tmf-relationship-list">
                    <li>No mention of case management</li>
                    <li>Lack of info on services</li>
                    <li>Lack of enrollment chances on pantry hours</li>
                    <li>Surface-level conversations</li>
                  </ul>
                </div>
              </div>

              <p>
                That trust shows in how people interact. When someone hesitated about taking a second avocado, volunteers didn't just say yes. They suggested: "You know what goes well with avocados? These chips!" They'd walk people back inside, add items to bags themselves, make sure everyone got what they needed. The community extends beyond staff too. People shared bags when someone forgot to bring their own. Three friends on bikes worked together to balance groceries. "Happy birthday!" someone shouted across the parking lot to a person they'd just met inside. Strangers helping strangers, because that's the culture here.
              </p>

              <p>
                But here's what we noticed: those conversations stayed surface-level. Greetings. "Can I take two of these?" Directions about portion limits. Genuine warmth, but no mention of the case management office down the hall. No word about career counseling or benefits enrollment programs. The volunteers were warm and welcoming, but they weren't bridging the gap between emergency food and long-term support. <strong>The connection existed. The information didn't flow through it.</strong>
              </p>
            </div>

            <div className="tmf-relationships-insight">
              <span className="tmf-insight-label">The missed opportunity:</span> <strong>These relationships are infrastructure.</strong> Volunteers have earned trust over time. People listen to them. They come back week after week, and that consistency could be the perfect channel for sharing information about services that change lives. The foundation is there. It's just not being used to connect people to what they need most.
            </div>
          </motion.div>

          {/* Card 3: Data-driven system design */}
          <motion.div
            className="tmf-observation-card-system"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h3>When the System Works Against You</h3>

            <div className="tmf-system-stats-grid">
              <div className="tmf-system-stat-large">
                <div className="tmf-system-number">81</div>
                <div className="tmf-system-label">online slots per week</div>
              </div>
              <div className="tmf-system-stat-large tmf-stat-contrast">
                <div className="tmf-system-number">2,000</div>
                <div className="tmf-system-label">compete for those 81 slots</div>
              </div>
            </div>

            <p>
              We tried booking an online slot. Simple registration: name, date of birth, address optional. But the next two weeks? Fully booked. "Online slots open Mondays for the next week," Shelva explained. "They fill up in hours." Only 81 slots. An estimated 2,000 people fight for those slots every week but end up having to walk in instead, not by choice, but because online isn't an option for them. <strong>The easier system becomes a lottery most people lose.</strong>
            </p>

            <div className="tmf-system-flow">
              <div className="tmf-flow-step">
                <svg className="tmf-flow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="5" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 10v6m0 0l-3 4m3-4l3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="tmf-flow-label">Walk in</div>
                <div className="tmf-flow-detail">10-15 people in line</div>
              </div>
              <div className="tmf-flow-arrow">→</div>
              <div className="tmf-flow-step">
                <svg className="tmf-flow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="8" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 13h18M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <div className="tmf-flow-label">Get bag</div>
                <div className="tmf-flow-detail">Focus on food</div>
              </div>
              <div className="tmf-flow-arrow">→</div>
              <div className="tmf-flow-step">
                <svg className="tmf-flow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <div className="tmf-flow-label">Less than 10 mins</div>
                <div className="tmf-flow-detail">In and out</div>
              </div>
              <div className="tmf-flow-arrow">→</div>
              <div className="tmf-flow-step">
                <svg className="tmf-flow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 4v16M15 12l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="tmf-flow-label">Leave</div>
                <div className="tmf-flow-detail">Mission complete</div>
              </div>
            </div>

            <p>
              So most people walk in. They wait outside in lines of 10-15 people. Staff regulate how many can shop at once. Once inside, they're given a bag and move quickly. Less than ten minutes on average. Eyes on the racks, checking expiration dates on fresh items, making decisions about what fits in the bag they can carry. Coordinators explain portion limits at the door. Regular visitors know the routine. First-timers don't. Some stood outside for minutes, unsure if they could just walk in. One woman asked us which door to use. Another asked to borrow our phone but was directed to the main building. Even asking for help required knowing where to go.
            </p>

            <div className="tmf-system-insight">
              <span className="tmf-insight-label">The systemic issue:</span> Access is hard. Awareness is harder. When you're navigating online booking systems that fill up in hours, or waiting in line, or focused on getting food into a bag you can carry home, there's no space to discover that this place offers far more than groceries. <strong>The system isn't designed to inform. It's designed to distribute.</strong>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FindingsSection;
